// Public API for @nostr-gives/share-formatter.
//
// Format spec: Vault/Architecture/Share Format.md
//
//   <NAME> #<NUM> <SCORE>/<MAX>
//   <emoji grid>
//
//   nostr.gives

import { emojiGrid, groupBars } from './emoji-grid.js';
import type { ShareNote } from './types.js';

export { emojiGrid, groupBars };
export type {
  GroupBar,
  GroupTier,
  NIP30EmojiTag,
  ShareNote,
  TileResult,
  TileState,
} from './types.js';

const FOOTER = 'nostr.gives';

/**
 * Format the canonical share text for a kind 1 note. Pure function:
 * no I/O, no signing, no network.
 */
export function formatShareText(note: ShareNote): string {
  const title = buildTitleLine(note);
  const grid = buildGridBlock(note);

  // Title + grid joined by a single newline; blank line then footer.
  // Per spec there is exactly one blank line between grid and footer.
  const parts: string[] = [title];
  if (grid.length > 0) {
    parts.push(grid);
  }
  parts.push('', FOOTER);
  return parts.join('\n');
}

function buildTitleLine(note: ShareNote): string {
  const head = `${note.gameName} #${note.puzzleNumber}`;
  const score = buildScoreFragment(note);
  return score.length > 0 ? `${head} ${score}` : head;
}

function buildScoreFragment(note: ShareNote): string {
  // Games like Connections, Spelling Bee, Chess have no n/N line - the
  // emoji grid or move count carries the result. Caller signals this by
  // passing `maxScore: null`.
  if (note.maxScore === null) {
    return note.scoreSuffix ? note.scoreSuffix.trim() : '';
  }

  const numerator = note.score === null ? 'X' : String(note.score);
  const denominator = String(note.maxScore);
  const hardMark = note.hardMode ? '*' : '';
  const suffix = note.scoreSuffix ?? '';
  return `${numerator}/${denominator}${hardMark}${suffix}`;
}

function buildGridBlock(note: ShareNote): string {
  if (typeof note.gridText === 'string') {
    return note.gridText;
  }
  if (note.gridRows && note.gridRows.length > 0) {
    return emojiGrid(note.gridRows);
  }
  if (note.groupBars && note.groupBars.length > 0) {
    return groupBars(note.groupBars);
  }
  return '';
}
